// Decode a Keycloak JWT (no signature check — verification is the API's job).
// Used to read orgId + realm roles client-side without an extra round-trip
// to /v1/auth/me on every render.
export interface IKeycloakClaims {
  sub: string;
  email?: string;
  name?: string;
  preferred_username?: string;
  given_name?: string;
  family_name?: string;
  /** Keycloak Organizations claim — { [orgName]: { id: string } }. */
  organization?: Record<string, { id?: string }>;
  /** Formant Robotics claim — present instead of `organization` when the token
   *  was issued by Robotics rather than cortex Keycloak. */
  'formant:claims'?: { organizationId?: string; userId?: string; isSuperuser?: boolean };
  /** Realm-level roles (includes `superuser` for global admins). */
  realm_access?: { roles?: string[] };
  /** Client-level roles. */
  resource_access?: Record<string, { roles?: string[] }>;
  exp?: number;
  iat?: number;
  iss?: string;
  aud?: string | string[];
}

export function decodeJwt(token: string | null | undefined): IKeycloakClaims | null {
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  const raw = parts[1];
  if (!raw) return null;
  try {
    const payload = raw.replace(/-/g, '+').replace(/_/g, '/');
    const padded = payload.padEnd(payload.length + ((4 - (payload.length % 4)) % 4), '=');
    return JSON.parse(decodeURIComponent(escape(atob(padded)))) as IKeycloakClaims;
  } catch (err: unknown) {
    console.info('console/jwt:decodeJwt', err);
    return null;
  }
}

/**
 * The caller's org id, from whichever authority issued the token: the Keycloak
 * Organizations claim (first membership) or the Formant Robotics
 * `formant:claims.organizationId`. Null when neither is present.
 */
export function extractOrgId(claims: IKeycloakClaims | null): string | null {
  if (!claims) return null;
  if (claims.organization) {
    const first = Object.values(claims.organization)[0];
    if (first?.id) return first.id;
  }
  return claims['formant:claims']?.organizationId ?? null;
}

export function hasRealmRole(claims: IKeycloakClaims | null, role: string): boolean {
  return Boolean(claims?.realm_access?.roles?.includes(role));
}

function isFormantEmail(email: string | null | undefined): boolean {
  return email?.trim().toLowerCase().endsWith('@formant.io') ?? false;
}

/** Platform superuser — Keycloak `superuser` realm role, or the Robotics
 *  token's `formant:claims.isSuperuser`. */
export function isSuperuserFromClaims(claims: IKeycloakClaims | null): boolean {
  if (!claims) return false;
  return hasRealmRole(claims, 'superuser') || claims['formant:claims']?.isSuperuser === true;
}

/**
 * Formant staff — a `@formant.io` account (session email or token claim) or a
 * platform superuser. Same rule as the API's `FormantStaffGuard`; gates the
 * cross-org Platform Costs view.
 */
export function isFormantStaff(
  sessionEmail: string | null | undefined,
  claims: IKeycloakClaims | null,
): boolean {
  return (
    isFormantEmail(sessionEmail) || isFormantEmail(claims?.email) || isSuperuserFromClaims(claims)
  );
}

/**
 * Org role driving console nav/capability gating.
 *
 * POC: any Robotics-authenticated user (token carries `formant:claims`) is
 * treated as FDE for now — the Robotics identity doesn't yet map to the
 * cortex org-role model. Keycloak tokens keep the existing derivation
 * (fde/superuser realm role or `@formant.io` email → fde, else customer).
 */
export function orgRoleFromClaims(claims: IKeycloakClaims | null): 'customer' | 'fde' {
  if (!claims) return 'customer';
  if (claims['formant:claims']) return 'fde';
  if (hasRealmRole(claims, 'fde') || hasRealmRole(claims, 'superuser')) return 'fde';
  return isFormantEmail(claims.email) ? 'fde' : 'customer';
}
